import React from 'react';
import { MsFormulaire } from '../types';
import { CHAMP_PARTICULARITES_MAX } from '../domain/operationConsultation';

interface OperationParticularitesProps {
  formData: MsFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: MsFormulaire) => void;
}

interface ChampDef {
  cle: keyof MsFormulaire;
  label: string;
  placeholder: string;
  type: 'select' | 'text' | 'textarea';
  requis: boolean;
  options?: string[];
}

// Sous-bloc "Particularites administratives" (refonte xlsx). 6 champs, 2 colonnes (span 6).
const CHAMPS: ChampDef[] = [
  {
    cle: 'niveauSensibilite',
    label: 'Niveau de sensibilité des informations liées à cette opération ?',
    placeholder: 'Sélectionnez…',
    type: 'select',
    requis: true,
    options: ['Public (P)', 'Diffusion restreinte (DR)', 'Secret (S)', 'Très Secret (TS)'],
  },
  {
    cle: 'visiteObligatoire',
    label: 'Visite préalable obligatoire ?',
    placeholder: 'ex : Non / Oui entre le 24/09/28 et le 27/09/28',
    type: 'text',
    requis: true,
  },
  {
    cle: 'verificationIdentites',
    label: "Vérification préalable des identités pour accéder à l'opération ?",
    placeholder: "ex : Non / Oui (transmettre la copie de vos pièces d'identités 48h à l'avance)",
    type: 'text',
    requis: true,
  },
  {
    cle: 'habilitation',
    label: 'Habilitation particulière à présenter ?',
    placeholder: "ex : Non / Oui (habilitation d'intervention sur site nucléaire obligatoire)",
    type: 'text',
    requis: true,
  },
  {
    cle: 'charteReglementation',
    label: 'Charte et/ou règlementation particulière imposée sur cette opération ?',
    placeholder: 'ex : Charte de gestion du bruit à respecter impérativement (voir lien URL)',
    type: 'textarea',
    requis: false,
  },
  {
    cle: 'clauseSuiviEnvironnemental',
    label: 'Clause de suivi environnemental et social ?',
    placeholder: 'ex : le tableau de suivi de la gestion des déchets doit se faire sur le fichier imposé (voir lien URL)',
    type: 'textarea',
    requis: false,
  },
];

const OperationParticularites: React.FC<OperationParticularitesProps> = ({ formData, viewMode, errors = {}, setFormData }) => {
  const update = (cle: keyof MsFormulaire, value: string) => {
    if (setFormData) setFormData({ ...formData, [cle]: value });
  };

  return (
    <div className="opms__admin-grid">
      {CHAMPS.map(({ cle, label, placeholder, type, requis, options }) => {
        const key = cle as string;
        const valeur = String(formData[cle] ?? '');
        const erreur = errors[key];
        return (
          <div className="form-group" style={{ gridColumn: 'span 6' }} key={key}>
            <label>
              {label} {!viewMode && requis && <span style={{ color: 'red' }}>*</span>}
            </label>
            {viewMode ? (
              <div className="input-inactif">{valeur || '-'}</div>
            ) : type === 'select' ? (
              <>
                <select
                  value={valeur}
                  onChange={(e) => update(cle, e.target.value)}
                  className={erreur ? 'error' : ''}
                >
                  {/* Placeholder non-selectionnable quand le champ est requis : le niveau de
                      sensibilite ne peut pas etre vide (defaut Public (P) pre-selectionne). */}
                  <option value="" disabled={requis}>{placeholder}</option>
                  {(options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            ) : type === 'textarea' ? (
              <>
                <textarea
                  value={valeur}
                  onChange={(e) => update(cle, e.target.value)}
                  className={erreur ? 'error' : ''}
                  placeholder={placeholder}
                  rows={3}
                  maxLength={CHAMP_PARTICULARITES_MAX}
                />
                <span className="opms__char-count">{valeur.length}/{CHAMP_PARTICULARITES_MAX}</span>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={valeur}
                  onChange={(e) => update(cle, e.target.value)}
                  className={erreur ? 'error' : ''}
                  placeholder={placeholder}
                  maxLength={CHAMP_PARTICULARITES_MAX}
                />
                <span className="opms__char-count">{valeur.length}/{CHAMP_PARTICULARITES_MAX}</span>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OperationParticularites;
